/**
 * Utilities for exporting diagrams as PNG (copy to clipboard / download).
 * Used by MermaidDiagram and VisualizerRenderer.
 */

const DEFAULT_SCALE = 2; // retina quality

/**
 * Converts an SVG string to a PNG Blob via offscreen canvas.
 * Extracts dimensions from the SVG's width/height or viewBox attributes.
 */
export function svgToPngBlob(svgString: string, scale = DEFAULT_SCALE): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svgEl = doc.querySelector('svg');
    if (!svgEl) {
      reject(new Error('No <svg> element found'));
      return;
    }

    // Resolve dimensions from explicit attributes or viewBox
    let width = parseFloat(svgEl.getAttribute('width') ?? '0');
    let height = parseFloat(svgEl.getAttribute('height') ?? '0');

    const viewBox = svgEl.getAttribute('viewBox');
    if ((!width || !height) && viewBox) {
      const parts = viewBox.split(/[\s,]+/);
      if (parts.length === 4) {
        width = width || parseFloat(parts[2] ?? '0');
        height = height || parseFloat(parts[3] ?? '0');
      }
    }

    // Fallback to reasonable defaults
    if (!width) width = 800;
    if (!height) height = 600;

    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas 2D context unavailable'));
      return;
    }

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('canvas.toBlob returned null'));
      }, 'image/png');
    };
    img.onerror = () => reject(new Error('Failed to load SVG as image'));

    // Encode SVG as data URI — ensure namespace is present
    if (!svgEl.getAttribute('xmlns')) {
      svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    }
    const serialized = new XMLSerializer().serializeToString(svgEl);
    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(serialized)))}`;
  });
}


/** Copies a PNG Blob to the clipboard. Falls back to plain text if unsupported. */
export async function copyPngToClipboard(blob: Blob, fallbackText?: string): Promise<void> {
  if (navigator.clipboard && window.ClipboardItem) {
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob }),
    ]);
    return;
  }
  // Fallback: copy source text
  if (fallbackText) {
    await navigator.clipboard.writeText(fallbackText);
  }
}
